import type { Track, TrackSourceKind } from '@/lib/db';

import { airTimeMinutes, type FlightSummary, summaryLogbookStatus } from './summary';

/**
 * Search, filter and sort over an already-fetched list of tracks — all of it
 * client-side, the same trade-off `TrackList.tsx` already made when it capped
 * itself at a fixed number of rows rather than paging through IndexedDB.
 */

export type SourceFilter = TrackSourceKind | 'all';

export function isSourceFilter(value: string, sources: readonly TrackSourceKind[]): boolean {
  return value === 'all' || (sources as readonly string[]).includes(value);
}

/** Registration, callsign or the source filename — whichever a pilot would type to find a flight again. */
export function matchesQuery(track: Track, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return [track.registration, track.callsign, track.sourceRef].some((field) =>
    field?.toLowerCase().includes(needle),
  );
}

export function matchesSource(track: Track, source: SourceFilter): boolean {
  return source === 'all' || track.source === source;
}

export type SortId = 'recent' | 'oldest' | 'longest' | 'most-fixes';

export const SORT_OPTIONS: readonly { id: SortId; label: string }[] = [
  { id: 'recent', label: 'Most recent' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'longest', label: 'Longest flight' },
  { id: 'most-fixes', label: 'Most fixes' },
];

export function isSortId(value: string): value is SortId {
  return SORT_OPTIONS.some((option) => option.id === value);
}

function durationMs(track: Track): number {
  return Date.parse(track.endTime) - Date.parse(track.startTime);
}

/** A new array — `Array.prototype.sort` mutates, and every caller here holds a list from a live query. */
export function sortTracks(tracks: readonly Track[], sort: SortId): Track[] {
  const copy = [...tracks];
  switch (sort) {
    case 'recent':
      return copy.sort((a, b) => b.startTime.localeCompare(a.startTime));
    case 'oldest':
      return copy.sort((a, b) => a.startTime.localeCompare(b.startTime));
    case 'longest':
      return copy.sort((a, b) => durationMs(b) - durationMs(a));
    case 'most-fixes':
      return copy.sort((a, b) => b.pointCount - a.pointCount);
  }
}

/*
 * The same three controls, applied to a joined `FlightSummary` rather than a
 * bare `Track`.
 *
 * The list grew a route, a logbook status and derived numbers when flights and
 * tracks were joined; search and sort have to see those too, or the search box
 * stops finding the flight a pilot actually remembers ("the one into NZTG").
 * The track-only functions above stay: they are what these delegate to, and
 * the export composer still works in tracks.
 */

/** Registration, callsign, filename, or either end of the route. */
export function matchesSummaryQuery(summary: FlightSummary, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  if (summary.track !== undefined && matchesQuery(summary.track, query)) {
    return true;
  }
  return [summary.registration, summary.from, summary.to, summary.entry?.remarks].some((field) =>
    field?.toLowerCase().includes(needle),
  );
}

/**
 * A summary with no track has no source to filter on, so any filter but `all`
 * excludes it — an entry typed by hand is genuinely not an FR24 import.
 */
export function matchesSummarySource(summary: FlightSummary, source: SourceFilter): boolean {
  if (source === 'all') {
    return true;
  }
  return summary.track !== undefined && matchesSource(summary.track, source);
}

export type LogbookFilter = 'all' | 'draft' | 'approved' | 'none' | 'merged';

export const LOGBOOK_FILTER_OPTIONS: readonly { id: LogbookFilter; label: string }[] = [
  { id: 'all', label: 'Any status' },
  { id: 'draft', label: 'Draft' },
  { id: 'approved', label: 'Approved' },
  { id: 'none', label: 'No entry' },
  { id: 'merged', label: 'Merged' },
];

export function isLogbookFilter(value: string): value is LogbookFilter {
  return LOGBOOK_FILTER_OPTIONS.some((option) => option.id === value);
}

export function matchesLogbook(summary: FlightSummary, filter: LogbookFilter): boolean {
  return filter === 'all' || summaryLogbookStatus(summary) === filter;
}

function summaryDurationMs(summary: FlightSummary): number {
  const minutes = airTimeMinutes(summary);
  if (minutes !== undefined) {
    return minutes * 60_000;
  }
  if (summary.startTime === undefined || summary.endTime === undefined) {
    return 0;
  }
  return Date.parse(summary.endTime) - Date.parse(summary.startTime);
}

/** A new array: every caller holds a list straight off a live query. */
export function sortSummaries(summaries: readonly FlightSummary[], sort: SortId): FlightSummary[] {
  const copy = [...summaries];
  switch (sort) {
    case 'recent':
      return copy.sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
    case 'oldest':
      return copy.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    case 'longest':
      return copy.sort((a, b) => summaryDurationMs(b) - summaryDurationMs(a));
    case 'most-fixes':
      return copy.sort((a, b) => (b.track?.pointCount ?? 0) - (a.track?.pointCount ?? 0));
  }
}

/** Start time where there is one, else the calendar date — both sort as text. */
function sortKey(summary: FlightSummary): string {
  return summary.startTime ?? `${summary.date}T00:00:00.000Z`;
}
